import { Modal } from 'react-bootstrap';
import { Link, useHistory, useLocation } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useTranslation } from 'react-i18next';
import { getPrincipalKey } from '../manage/dashboardClassCreationNavigation';
import { readSettlementView, SETTLEMENT_REPORT_PATH } from './settlementNavigation';
import StudentSettlementSummary from './StudentSettlementSummary';
import SettlementExtraFees from './SettlementExtraFees';
import ClientDiscountService from '../../services/ClientDiscountService';
import ClientLedgerService from '../../services/ClientLedgerService';
import SettlementSummary from './SettlementSummary';
import summarizeSettlements from './clientSettlementSummary';
import { monthNames } from '../../i18n/calendarLabels';
import { interfaceError } from '../../i18n/errorMessages';
import toast from '../../i18n/toast';
import LocalizedMessage from '../common/LocalizedMessage';
import { displayDate } from "../common/datePresentation";
import { formatDate } from "../common/Helpers";
import * as clientActions from "../../redux/actions/clientActions";
import * as clientLedgerActions from "../../redux/actions/clientLedgerActions";
import * as enrollmentActions from "../../redux/actions/enrollmentActions";
import * as lessonRegisterActions from "../../redux/actions/lessonRegisterActions";
import * as schoolClassActions from "../../redux/actions/schoolClassActions";
import * as studentActions from "../../redux/actions/studentActions";
import * as classStudentsActions from "../../redux/actions/classStudentsActions";
import * as teacherActions from "../../redux/actions/teacherActions";
import SettingsService from "../../services/SettingsService";
import API from "../../services/SchoolClassService";
import { isFinalizedAttendance } from "../../../LambdaFunctions/shared/lessonAttendance";
import { buildManageRouteWithSchoolYear } from "../manage/manageSchoolYearRouting";
import { CLIENT_LEDGER_ENTRY_TYPES, formatLedgerAmount } from "../manage/clientLedgerTypes";
import ClientSettlementsReportForm from "./ClientSettlementsReportForm";
import ClientSettlementsList from "./ClientSettlementsList";
import ClientPaymentsModal from "./ClientPaymentsModal";
import buildSchoolYearMonthOptions from "./clientSettlementsMonthOptions";
import { calculateClientSettlements, collectMissingClientWarnings, getSettlementItemKey, recalcWithExclusions, withSettlementFees } from "./clientLedgerCalculations";
import { getMonthlyClientPayments } from "./clientSettlementPayments";
import {
  enrichSettlementsWithWatchdog,
  filterWatchdogSettlements,
  getPaymentWatchdogFollowUpKey,
  PAYMENT_WATCHDOG_FOLLOW_UP_STATUSES,
  PAYMENT_WATCHDOG_SETTING_ID,
} from "./clientSettlementWatchdog";
import { buildSchoolYearRange, getCurrentSchoolYearStartYear } from "../common/schoolYear";
import { DEFAULT_LESSON_TYPES_SETTING, getLessonTypesSetting } from "../common/lessonTypes";

const currentMonth = () => {
  const currentDate = new Date();
  return `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}`;
};

const LAST_SETTLEMENT_MONTH_STORAGE_KEY = "clientSettlementsReport.month";

const readLastSettlementMonth = () => {
  try {
    return window.localStorage.getItem(LAST_SETTLEMENT_MONTH_STORAGE_KEY);
  } catch (error) {
    return null;
  }
};

const saveLastSettlementMonth = month => {
  try {
    window.localStorage.setItem(LAST_SETTLEMENT_MONTH_STORAGE_KEY, month);
  } catch (error) {
    // Browser settings are only a convenience; the report should keep working without them.
  }
};

const getDefaultSettlementMonth = (activeSchoolYearStartYear, today = new Date()) => {
  const currentSchoolYearStartYear = getCurrentSchoolYearStartYear(today);

  if (activeSchoolYearStartYear === currentSchoolYearStartYear) {
    return currentMonth();
  }

  return `${activeSchoolYearStartYear + 1}-08`;
};

const getInitialSettlementMonth = (activeSchoolYearStartYear, monthOptions) => {
  const savedMonth = readLastSettlementMonth();
  if (savedMonth && monthOptions.some(option => option.value === savedMonth)) {
    return savedMonth;
  }

  return getDefaultSettlementMonth(activeSchoolYearStartYear);
};

const makeKey = (clientId, itemKey) => `${clientId}#${itemKey}`;
const clientKeyPrefix = clientId => `${clientId}#`;

const stringifyWarningRecord = record => (record ? JSON.stringify(record) : "brak");
const getMissingClientWarningKey = warning => `${warning.lessonRegisterId}#${warning.studentId}`;
const SORT_DIRECTION = {
  ASCENDING: "ascending",
  DESCENDING: "descending",
};

const DEFAULT_SORT_CONFIG = {
  key: "closingBalance",
  direction: SORT_DIRECTION.ASCENDING,
};

const DEFAULT_WATCHDOG_FILTERS = {
  enabledOnly: false,
  minAmount: "",
  minOverdueDays: "",
  priority: "",
  followUpStatus: "",
};

const defaultLoadEnrollments = () => Promise.resolve();

const getPaymentWatchdogCases = settings => {
  const settingsList = Array.isArray(settings) ? settings : [settings];
  const watchdogSetting = settingsList.find(setting => setting && setting.id === PAYMENT_WATCHDOG_SETTING_ID);
  return watchdogSetting && watchdogSetting.cases ? watchdogSetting.cases : {};
};

const isSettlementEmpty = settlement => {
  const snapshot = settlement.snapshot || {};
  const hasLessons = (snapshot.charged || []).length > 0 || (snapshot.skipped || []).length > 0;
  const hasWarnings = (snapshot.warnings || []).length > 0;
  const hasFinancialActivity = [
    settlement.openingBalance,
    settlement.paymentsInMonth,
    settlement.monthlyChargeAmount,
    settlement.closingBalance,
  ].some(value => Number(value || 0) !== 0);

  return !hasLessons && !hasWarnings && !hasFinancialActivity;
};

const matchesWorkflowFilter = (settlement, report) => {
  if (report.showDraft === report.showApproved) return true;
  if (report.showDraft) return !settlement.approved;
  return settlement.approved;
};

const matchesNegativeBalanceFilter = (settlement, report) => (
  !report.showNegativeBalanceOnly || Number(settlement.closingBalance || 0) < 0
);

const getSettlementStatusLabel = settlement => (settlement.approved ? "Zatwierdzone" : "Robocze");

const compareStrings = (left, right, direction) => {
  const comparison = String(left || "").toLowerCase().localeCompare(String(right || "").toLowerCase());
  return direction === SORT_DIRECTION.ASCENDING ? comparison : -comparison;
};

const compareNumbers = (left, right, direction) => {
  const delta = Number(left || 0) - Number(right || 0);
  return direction === SORT_DIRECTION.ASCENDING ? delta : -delta;
};

const compareDisplaySettlements = (left, right, sortConfig) => {
  switch (sortConfig.key) {
    case "clientName":
      return compareStrings(left.clientName, right.clientName, sortConfig.direction);
    case "openingBalance":
      return compareNumbers(left.openingBalance, right.openingBalance, sortConfig.direction);
    case "paymentsInMonth":
      return compareNumbers(left.paymentsInMonth, right.paymentsInMonth, sortConfig.direction);
    case "monthlyChargeAmount":
      return compareNumbers(left.summaryMonthlyChargeAmount, right.summaryMonthlyChargeAmount, sortConfig.direction);
    case "status":
      return compareStrings(left.statusLabel, right.statusLabel, sortConfig.direction);
    case "closingBalance":
    default:
      return compareNumbers(left.summaryClosingBalance, right.summaryClosingBalance, sortConfig.direction);
  }
};

export function ClientSettlementsReportPage({
  clients,
  students,
  classStudents,
  teachers,
  schoolClasses,
  lessonRegister,
  enrollments,
  clientLedger,
  clientOptions,
  loadClients,
  loadStudents,
  loadClassStudents,
  loadTeachers,
  loadSchoolClasses,
  loadLessonRegisters,
  loadEnrollments,
  loadClientLedger,
  saveClientLedgerEntry,
  deleteClientLedgerEntry,
  replaceMonthlyCharge,
  activeSchoolYearStartYear,
  principalKey,
  settingsService,
  discountService,
  settlementService,
}) {
  const { t: tr, i18n } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const restoredView = useRef(readSettlementView(location, principalKey, activeSchoolYearStartYear)).current;
  const expansionState = useRef((restoredView && restoredView.expansionState) || {});
  const rememberExpansion = useCallback(value => { expansionState.current = value; }, []);
  const pendingScroll = useRef(restoredView && restoredView.scroll);

  const activeSchoolYearRange = useMemo(
    () => buildSchoolYearRange(activeSchoolYearStartYear),
    [activeSchoolYearStartYear],
  );
  const monthOptions = useMemo(
    () => buildSchoolYearMonthOptions(activeSchoolYearRange),
    [activeSchoolYearRange],
  );
  const [report, setReport] = useState(() => (restoredView && restoredView.report) || {
    month: getInitialSettlementMonth(activeSchoolYearStartYear, monthOptions),
    clientId: "all",
    showDraft: false,
    showApproved: false,
    showNegativeBalanceOnly: false,
    showEmptyClients: false,
    watchdog: { ...DEFAULT_WATCHDOG_FILTERS },
    sortConfig: { ...DEFAULT_SORT_CONFIG },
  });
  const [summaryLoads, setSummaryLoads] = useState({
    clients: clients.length ? 'ready' : 'loading',
    students: students.length ? 'ready' : 'loading',
    classStudents: classStudents.length ? 'ready' : 'loading',
    teachers: teachers.length ? 'ready' : 'loading',
    classes: 'loading',
    lessons: 'loading',
    ledger: 'loading',
    settings: 'loading',
    discounts: 'loading',
    enrollments: 'ready',
  });
  const summaryRequests = useRef({});
  const trackSummaryLoad = useCallback((key, request) => {
    const requestId = (summaryRequests.current[key] || 0) + 1;
    summaryRequests.current[key] = requestId;
    setSummaryLoads(current => ({ ...current, [key]: 'loading' }));
    return request.then(value => {
      if (summaryRequests.current[key] === requestId) setSummaryLoads(current => ({ ...current, [key]: 'ready' }));
      return value;
    }, error => {
      if (summaryRequests.current[key] === requestId) setSummaryLoads(current => ({ ...current, [key]: 'error' }));
      throw error;
    });
  }, []);
  const [discountPolicy, setDiscountPolicy] = useState({ types: [], grants: [] });
  const [feeStates, setFeeStates] = useState({});
  const [feeReload, setFeeReload] = useState(0);
  const [feeLoadError, setFeeLoadError] = useState(false);
  const feeScopeKey = `${principalKey}:${activeSchoolYearStartYear}:${report.month}`;
  const activeFeeScope = useRef(feeScopeKey);
  activeFeeScope.current = feeScopeKey;
  useEffect(() => {
    let active = true;
    setFeeStates({}); setFeeLoadError(false);
    if (!settlementService.getSettlementFees) return () => { active = false; };
    const load = async () => {
      const candidates = clients.filter(client => !client.deleted);
      const result = {};
      for (let index = 0; index < candidates.length; index += 4) {
        // eslint-disable-next-line no-await-in-loop
        await Promise.all(candidates.slice(index, index + 4).map(async client => {
          try { result[client.id] = await settlementService.getSettlementFees({ clientId: client.id, month: report.month, schoolYearStartYear: activeSchoolYearStartYear }); } catch (error) {
            // Assignment-pending clients retain their existing explicit blocked state.
            if (error && error.message === 'BILLING_ASSIGNMENT_PENDING') return;
            throw error;
          }
        }));
      }
      if (active) setFeeStates(result);
    };
    trackSummaryLoad('fees', load()).catch(() => { if (active) setFeeLoadError(true); });
    return () => { active = false; };
  }, [clients, report.month, activeSchoolYearStartYear, principalKey, settlementService, feeReload, trackSummaryLoad]);
  const [approval, setApproval] = useState(null);
  const [approvalBusy, setApprovalBusy] = useState(false);
  const [approvalError, setApprovalError] = useState('');
  const [approvalConflict, setApprovalConflict] = useState(false);
  const approvalRequest = useRef(0);
  const approvalInFlight = useRef(false);
  useEffect(() => {
    approvalRequest.current += 1;
    approvalInFlight.current = false;
    setApproval(null);
    setApprovalBusy(false);
    setApprovalError('');
    return () => { approvalRequest.current += 1; };
  }, [report.month, activeSchoolYearStartYear]);
  useEffect(() => {
    let active = true;
    const load = async () => {
      const types = await discountService.getTypes();
      const grants = [];
      let cursor;
      do {
        // eslint-disable-next-line no-await-in-loop
        const page = await discountService.getGrants(undefined, cursor);
        grants.push(...page.versions);
        cursor = page.nextCursor;
      } while (cursor);
      if (active) setDiscountPolicy({ types: types.versions, grants });
    };
    trackSummaryLoad('discounts', load()).catch(() => {});
    return () => { active = false; };
  }, [discountService, trackSummaryLoad]);
  const [excludedKeys, setExcludedKeys] = useState((restoredView && restoredView.excludedKeys) || {});
  const [paymentsModal, setPaymentsModal] = useState({
    isOpen: false,
    clientId: "",
    month: "",
  });
  const [warningsExpanded, setWarningsExpanded] = useState(!!(restoredView && restoredView.warningsExpanded));
  const [watchdogFollowUps, setWatchdogFollowUps] = useState({});
  const [watchdogDrafts, setWatchdogDrafts] = useState((restoredView && restoredView.watchdogDrafts) || {});
  const [watchdogMessage, setWatchdogMessage] = useState("");
  const [watchdogError, setWatchdogError] = useState("");
  const [lessonTypes, setLessonTypes] = useState(DEFAULT_LESSON_TYPES_SETTING);
  const [enrollmentsLoaded, setEnrollmentsLoaded] = useState(
    () => enrollments.length > 0 || loadEnrollments === defaultLoadEnrollments,
  );
  const [enrollmentsLoadRequested, setEnrollmentsLoadRequested] = useState(false);

  useEffect(() => {
    setReport(currentReport => ({
      ...currentReport,
      month: monthOptions.some(option => option.value === currentReport.month) ? currentReport.month : getInitialSettlementMonth(activeSchoolYearStartYear, monthOptions),
    }));
  }, [activeSchoolYearStartYear, monthOptions]);

  useEffect(() => {
    if (clients.length === 0) {
      trackSummaryLoad('clients', loadClients()).catch(error => toast.error(interfaceError(error, 'common:errors.load')));
    }
    if (students.length === 0) {
      trackSummaryLoad('students', loadStudents()).catch(error => toast.error(interfaceError(error, 'common:errors.load')));
    }
    trackSummaryLoad('classes', loadSchoolClasses(activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate))
      .catch(error => toast.error(interfaceError(error, 'common:errors.load')));
    trackSummaryLoad('classStudents', loadClassStudents(activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate))
      .catch(error => toast.error(interfaceError(error, 'common:errors.load')));
    if (teachers.length === 0) {
      trackSummaryLoad('teachers', loadTeachers()).catch(error => toast.error(interfaceError(error, 'common:errors.load')));
    }
  }, [
    trackSummaryLoad,
    clients.length,
    loadClients,
    loadSchoolClasses,
    loadClassStudents,
    loadStudents,
    loadTeachers,
    students.length,
    teachers.length,
    activeSchoolYearRange.fromDate,
    activeSchoolYearRange.tillDate,
  ]);

  useEffect(() => {
    if (enrollments.length > 0) {
      setEnrollmentsLoaded(true);
      return;
    }

    if (enrollmentsLoaded || enrollmentsLoadRequested) {
      return;
    }

    setEnrollmentsLoadRequested(true);
    trackSummaryLoad('enrollments', loadEnrollments())
      .then(() => setEnrollmentsLoaded(true))
      .catch(error => toast.error(interfaceError(error, 'common:errors.load')));
  }, [
    trackSummaryLoad,
    enrollments.length,
    enrollmentsLoaded,
    enrollmentsLoadRequested,
    loadEnrollments,
  ]);

  useEffect(() => {
    trackSummaryLoad('lessons', loadLessonRegisters(activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate))
      .catch(error => toast.error(interfaceError(error, 'common:errors.load')));
  }, [
    activeSchoolYearRange.fromDate,
    activeSchoolYearRange.tillDate,
    loadLessonRegisters,
    trackSummaryLoad,
  ]);

  useEffect(() => {
    trackSummaryLoad('ledger', loadClientLedger()).catch(error => toast.error(interfaceError(error, 'common:errors.load')));
  }, [loadClientLedger, trackSummaryLoad]);

  useEffect(() => {
    Promise.resolve()
      .then(() => trackSummaryLoad('settings', settingsService.getSettings()))
      .then(settings => {
        const followUps = getPaymentWatchdogCases(settings);
        setWatchdogFollowUps(followUps);
        if (!restoredView) setWatchdogDrafts({});
        setLessonTypes(getLessonTypesSetting(settings));
      })
      .catch(error => {
        setSummaryLoads(current => ({ ...current, settings: 'error' }));
        if (typeof fetch === "undefined" && error && error.name === "ReferenceError") {
          return;
        }
        setWatchdogError(tr("manage:extraCouldNotLoadOverdueBalanceHandlingStatuses_119"));
      });
  }, [settingsService, trackSummaryLoad]);

  const baseSettlements = useMemo(() => calculateClientSettlements({
    clients,
    month: report.month,
    ledger: clientLedger,
    lessonRegister,
    students,
    schoolClasses,
    teachers,
    lessonTypes,
    enrollments,
    discountTypes: discountPolicy.types,
    discountGrants: discountPolicy.grants,
  }), [discountPolicy, clientLedger, clients, lessonRegister, report.month, schoolClasses, students, teachers, lessonTypes, enrollments]);

  const feeSettlements = useMemo(() => baseSettlements.map(settlement => {
    const state = feeStates[settlement.clientId];
    return !settlement.approved && state ? withSettlementFees(settlement, state.items) : settlement;
  }), [baseSettlements, feeStates]);
  const filteredSettlements = useMemo(() => feeSettlements
    .filter(settlement => report.clientId === "all" || settlement.clientId === report.clientId)
    .filter(settlement => matchesWorkflowFilter(settlement, report))
    .filter(settlement => matchesNegativeBalanceFilter(settlement, report))
    .filter(settlement => report.showEmptyClients || !isSettlementEmpty(settlement)), [
    feeSettlements,
    report.clientId,
    report.showDraft,
    report.showApproved,
    report.showNegativeBalanceOnly,
    report.showEmptyClients,
  ]);
  const reportWarnings = useMemo(() => collectMissingClientWarnings({
    month: report.month,
    lessonRegister,
    students,
  }), [lessonRegister, report.month, students]);
  const expansionResetKey = `${activeSchoolYearStartYear}:${report.month}`;

  const baseDisplaySettlements = useMemo(() => filteredSettlements
    .map(savedSettlement => {
      const lessons = recalcWithExclusions(savedSettlement, report.month, excludedKeys);
      // Recalculation yields lesson-only working totals; replace its fee metadata before applying the desired set.
      const state = feeStates[savedSettlement.clientId];
      const previewSettlement = state ? withSettlementFees({ ...lessons, snapshot: { ...lessons.snapshot, extraFeeTotalCents: 0 } }, state.items) : lessons;
      const sanitizedWarnings = (previewSettlement.snapshot.warnings || []).filter(warning => warning.reason !== "missingClient");
      const sanitizedPreviewSettlement = {
        ...previewSettlement,
        snapshot: {
          ...previewSettlement.snapshot,
          warnings: sanitizedWarnings,
        },
      };

      return {
        ...savedSettlement,
        statusLabel: getSettlementStatusLabel(savedSettlement),
        summaryMonthlyChargeAmount: savedSettlement.approved ? savedSettlement.monthlyChargeAmount : sanitizedPreviewSettlement.monthlyChargeAmount,
        summaryClosingBalance: savedSettlement.approved ? savedSettlement.closingBalance : sanitizedPreviewSettlement.closingBalance,
        previewMonthlyChargeAmount: sanitizedPreviewSettlement.monthlyChargeAmount,
        previewClosingBalance: sanitizedPreviewSettlement.closingBalance,
        previewSnapshot: sanitizedPreviewSettlement.snapshot,
        previewSettlement: sanitizedPreviewSettlement,
      };
    })
    .sort((left, right) => compareDisplaySettlements(left, right, report.sortConfig)), [
    excludedKeys,
    feeStates,
    filteredSettlements,
    report.month,
    report.sortConfig,
  ]);
  const enrichedSettlements = useMemo(() => enrichSettlementsWithWatchdog({
    settlements: baseDisplaySettlements,
    followUps: watchdogFollowUps,
    now: new Date(),
  }), [baseDisplaySettlements, watchdogFollowUps]);

  const settlementsWithAttendanceWarnings = useMemo(() => {
    const todayStr = formatDate(new Date());
    return enrichedSettlements.map(settlement => {
      const clientStudents = students.filter(s => s.clientId === settlement.clientId);
      const clientStudentIds = clientStudents.map(s => s.id);
      
      const relatedClassStudents = classStudents.filter(cs => clientStudentIds.includes(cs.studentId));
      const relatedClassIds = [...new Set(relatedClassStudents.map(cs => cs.schoolClassId))];
      
      const relatedSchoolClasses = schoolClasses.filter(sc => relatedClassIds.includes(sc.id));
      
      const newWarnings = [];
      
      relatedSchoolClasses.forEach(schoolClass => {
        const scheduledLessons = API.generateScheduledClasses(schoolClass, lessonRegister, students, classStudents);
        
        const missedLessons = scheduledLessons.filter(lesson => {
          const date = lesson.defaultLessonDetails.date;
          return date.startsWith(report.month) && 
                 date < todayStr && 
                 !isFinalizedAttendance(lesson.lessonRegister);
        });
        
        missedLessons.forEach(lesson => {
          newWarnings.push({
            reason: "missingAttendance",
            date: lesson.defaultLessonDetails.date,
            classId: schoolClass.id,
            lessonIndex: lesson.id.split('-').pop(),
            className: schoolClass.name || schoolClass.id,
          });
        });
      });
      
      if (newWarnings.length > 0) {
        return {
          ...settlement,
          snapshot: {
            ...settlement.snapshot,
            warnings: [...(settlement.snapshot?.warnings || []), ...newWarnings],
          },
          previewSnapshot: {
            ...settlement.previewSnapshot,
            warnings: [...(settlement.previewSnapshot?.warnings || []), ...newWarnings],
          },
        };
      }
      
      return settlement;
    });
  }, [enrichedSettlements, students, classStudents, schoolClasses, lessonRegister, report.month]);

  const watchdogFilteredSettlements = useMemo(() => filterWatchdogSettlements(
    settlementsWithAttendanceWarnings,
    report.watchdog,
  ), [settlementsWithAttendanceWarnings, report.watchdog]);

  const selectedClient = useMemo(() => clients.find(client => client.id === paymentsModal.clientId), [clients, paymentsModal.clientId]);
  const modalEntries = useMemo(() => (
    paymentsModal.isOpen
      ? getMonthlyClientPayments(clientLedger, paymentsModal.clientId, paymentsModal.month, activeSchoolYearStartYear)
      : []
  ), [clientLedger, paymentsModal.clientId, paymentsModal.isOpen, paymentsModal.month, activeSchoolYearStartYear]);

  const handleChange = event => {
    const { name, value } = event.target;

    if (name.startsWith("watchdog.")) {
      const watchdogField = name.split(".")[1];
      setReport(currentReport => ({
        ...currentReport,
        watchdog: {
          ...currentReport.watchdog,
          [watchdogField]: value,
        },
      }));
      return;
    }

    if (name === "month") {
      saveLastSettlementMonth(value);
    }

    setReport(currentReport => ({
      ...currentReport,
      [name]: value,
    }));
  };

  const handleSort = key => {
    setReport(currentReport => {
      const isSameKey = currentReport.sortConfig.key === key;
      const direction = isSameKey && currentReport.sortConfig.direction === SORT_DIRECTION.ASCENDING
        ? SORT_DIRECTION.DESCENDING
        : SORT_DIRECTION.ASCENDING;

      return {
        ...currentReport,
        sortConfig: {
          key,
          direction,
        },
      };
    });
  };

  const handleClearFilters = () => {
    setReport(currentReport => ({
      ...currentReport,
      clientId: "all",
      showDraft: false,
      showApproved: false,
      showNegativeBalanceOnly: false,
      showEmptyClients: false,
      watchdog: { ...DEFAULT_WATCHDOG_FILTERS },
      sortConfig: { ...DEFAULT_SORT_CONFIG },
    }));
  };

  useEffect(() => {
    if (paymentsModal.isOpen) {
      setPaymentsModal({
        isOpen: false,
        clientId: "",
        month: "",
      });
    }
  }, [report.clientId, report.month]);

  const lastFilterScope = useRef(`${report.clientId}:${report.month}`);
  useEffect(() => {
    const scope = `${report.clientId}:${report.month}`;
    if (lastFilterScope.current !== scope) setWarningsExpanded(false);
    lastFilterScope.current = scope;
  }, [report.clientId, report.month]);

  const handleToggleExclusion = (clientId, itemKey) => {
    const key = makeKey(clientId, itemKey);
    setExcludedKeys(prev => {
      const next = { ...prev };
      if (next[key]) {
        delete next[key];
      } else {
        next[key] = true;
      }
      return next;
    });
  };

  const handleApprove = (settlement, reason = '') => {
    if (approvalInFlight.current) return;
    if (settlementService.getSettlementFees && !feeStates[settlement.clientId]) {
      setApprovalError(tr('billing:fees.loading'));
      return;
    }
    if (!enrollmentsLoaded) {
      toast.info(tr("manage:extraEnrolmentsAndPackagesAreLoadingTryApprovingTheSettlement_120"));
      return;
    }

    const existingEntry = clientLedger.find(entry => (
      entry.clientId === settlement.clientId &&
      entry.type === CLIENT_LEDGER_ENTRY_TYPES.MONTHLY_CHARGE &&
      entry.settlementMonth === settlement.month
    ));
    const blockingWarnings = (settlement.snapshot.warnings || []).filter(warning => warning.reason === "missingRate");

    if (blockingWarnings.length > 0) {
      toast.info(tr("manage:extraCannotApproveTheSettlementWithoutADefinedRate_121"));
      return;
    }

    setApprovalBusy(true);
    approvalInFlight.current = true;
    const request = ++approvalRequest.current;
    setApprovalError('');
    setApprovalConflict(false);
    const input = { clientId: settlement.clientId,
      month: settlement.month,
      schoolYearStartYear: activeSchoolYearStartYear,
      itemSelections: [
        ...(settlement.snapshot.charged || []).map(item => ({ itemKey: getSettlementItemKey(item), charged: true })),
        ...(settlement.snapshot.skipped || []).map(item => ({ itemKey: getSettlementItemKey(item), charged: false })),
      ],
      correctionOf: existingEntry ? existingEntry.entryKey : null };
    if (existingEntry) {
      input.correctionReason = reason.trim() || (feeStates[settlement.clientId] && feeStates[settlement.clientId].correctionReason) || '';
      if (!input.correctionReason && ((existingEntry.snapshot && existingEntry.snapshot.extraFees) || []).length > 0) {
        setApprovalError(tr('billing:fees.invalidReason')); setApprovalBusy(false); approvalInFlight.current = false;
        return;
      }
      if (!input.correctionReason) input.correctionReason = 'Korekta rozliczenia lekcji';
    }
    settlementService.previewSettlement(input)
      .then(preview => {
        if (request === approvalRequest.current) setApproval({ ...preview, input, existingEntry, requestId: `settlement-${Date.now()}-${Math.random().toString(36).slice(2)}` });
      })
      .catch(() => { if (request === approvalRequest.current) setApprovalError(tr('billing:settlementApproval.failed')); })
      .finally(() => {
        if (request === approvalRequest.current) { setApprovalBusy(false); approvalInFlight.current = false; }
      });
  };

  const confirmApproval = () => {
    if (!approval || approvalInFlight.current) return;
    approvalInFlight.current = true;
    const request = ++approvalRequest.current;
    setApprovalBusy(true);
    setApprovalError('');
    replaceMonthlyCharge(approval.existingEntry, { ...approval.entry,
      previewToken: approval.previewToken,
      expectedBillingRevision: approval.expectedBillingRevision,
      requestId: approval.requestId })
      .then(() => {
        if (request !== approvalRequest.current) return;
        setFeeReload(value => value + 1);
        setExcludedKeys(currentKeys => Object.keys(currentKeys)
          .filter(key => !key.startsWith(clientKeyPrefix(approval.entry.clientId)))
          .reduce((nextKeys, key) => ({
            ...nextKeys,
            [key]: currentKeys[key],
          }), {}));
        setApproval(null);
        toast.info(tr("manage:extraSettlementApproved_122"));
      })
      .catch(error => {
        if (request === approvalRequest.current) {
          setApprovalConflict(error.code === 409);
          setApprovalError(tr(error.code === 409 ? 'billing:settlementApproval.changed' : 'billing:settlementApproval.failed'));
        }
      }).finally(() => {
        if (request === approvalRequest.current) { setApprovalBusy(false); approvalInFlight.current = false; }
      });
  };

  const refreshApproval = () => {
    if (!approval || approvalInFlight.current) return;
    approvalInFlight.current = true;
    const request = ++approvalRequest.current;
    setApprovalBusy(true);
    settlementService.previewSettlement(approval.input)
      .then(preview => {
        if (request !== approvalRequest.current) return;
        setApproval(current => ({ ...current, ...preview, requestId: `settlement-${Date.now()}-${Math.random().toString(36).slice(2)}` }));
        setApprovalError('');
        setApprovalConflict(false);
      })
      .catch(() => { if (request === approvalRequest.current) setApprovalError(tr('billing:settlementApproval.failed')); })
      .finally(() => {
        if (request === approvalRequest.current) { setApprovalBusy(false); approvalInFlight.current = false; }
      });
  };

  const handleOpenPayments = (clientId, month) => {
    setPaymentsModal({
      isOpen: true,
      clientId,
      month,
    });
  };

  const handleClosePayments = () => {
    setPaymentsModal({
      isOpen: false,
      clientId: "",
      month: "",
    });
  };

  const handleWatchdogDraftChange = (settlement, patch) => {
    const key = getPaymentWatchdogFollowUpKey(settlement.clientId, settlement.month);
    setWatchdogDrafts(currentDrafts => ({
      ...currentDrafts,
      [key]: {
        ...(currentDrafts[key] || {}),
        ...patch,
      },
    }));
  };

  const handleSaveWatchdogFollowUp = settlement => {
    const key = getPaymentWatchdogFollowUpKey(settlement.clientId, settlement.month);
    const existingRecord = watchdogFollowUps[key] || {};
    const draftRecord = watchdogDrafts[key] || {};
    const updatedAt = new Date().toISOString();
    const buildNextRecord = latestRecord => {
      const status = draftRecord.status || latestRecord.status || PAYMENT_WATCHDOG_FOLLOW_UP_STATUSES.TO_CONTACT;
      const note = draftRecord.note !== undefined ? draftRecord.note : latestRecord.note || "";

      return {
        ...latestRecord,
        ...draftRecord,
        clientId: settlement.clientId,
        settlementMonth: settlement.month,
        status,
        note,
        updatedAt,
        history: [
          ...(latestRecord.history || []),
          {
            status,
            note,
            updatedAt,
          },
        ],
      };
    };
    let fallbackFollowUps = null;

    setWatchdogError("");
    setWatchdogMessage("");

    return settingsService.getSettings()
      .then(settings => getPaymentWatchdogCases(settings))
      .then(latestFollowUps => {
        const nextRecord = buildNextRecord(latestFollowUps[key] || existingRecord);
        const nextFollowUps = {
          ...latestFollowUps,
          [key]: nextRecord,
        };
        fallbackFollowUps = nextFollowUps;

        return settingsService.saveSetting({
          id: PAYMENT_WATCHDOG_SETTING_ID,
          cases: nextFollowUps,
        });
      })
      .then(savedSetting => {
        const savedFollowUps = savedSetting && savedSetting.cases ? savedSetting.cases : fallbackFollowUps;
        setWatchdogFollowUps(savedFollowUps);
        setWatchdogDrafts(currentDrafts => {
          const nextDrafts = { ...currentDrafts };
          delete nextDrafts[key];
          return nextDrafts;
        });
        setWatchdogMessage(tr("manage:extraOverdueBalanceHandlingSaved_123"));
      })
      .catch(() => {
        setWatchdogError(tr("manage:extraCouldNotSaveOverdueBalanceHandling_124"));
      });
  };

  const getPersonLinkProps = (path, clientId) => {
    const [pathname, hash] = path.split('#');
    const to = { ...buildManageRouteWithSchoolYear(pathname, '', activeSchoolYearStartYear), hash: hash ? `#${hash}` : '' };
    return { to,
      onClick: event => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        const anchorId = `settlement-client-${clientId}`;
        const anchor = document.getElementById(anchorId);
        const settlementView = { principalKey,
          schoolYearStartYear: activeSchoolYearStartYear,
          report,
          excludedKeys,
          watchdogDrafts,
          warningsExpanded,
          expansionState: expansionState.current,
          scroll: { x: window.scrollX, y: window.scrollY, anchorId, anchorTop: anchor ? anchor.getBoundingClientRect().top : null },
        };
        const returnLocation = { ...buildManageRouteWithSchoolYear(SETTLEMENT_REPORT_PATH, location.search, activeSchoolYearStartYear),
          state: { ...location.state, settlementView },
        };
        history.replace(returnLocation);
        history.push({ ...to, state: { settlementReturn: returnLocation } });
      },
    };
  };

  useEffect(() => {
    if (!pendingScroll.current || !Object.values(summaryLoads).every(status => status === 'ready')) return undefined;
    const frame = window.requestAnimationFrame(() => {
      const scroll = pendingScroll.current;
      if (!scroll) return;
      const anchor = document.getElementById(scroll.anchorId);
      const top = anchor && scroll.anchorTop !== null ? window.scrollY + anchor.getBoundingClientRect().top - scroll.anchorTop : scroll.y;
      window.scrollTo(scroll.x, top);
      pendingScroll.current = null;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [summaryLoads, watchdogFilteredSettlements]);

  return (
    <div className="mb-5">
      <h2 className="manage-title">{tr("billing:clientSettlements_109")}</h2>
      <Link
        className="btn manage-button manage-teal-button mb-3"
        to={buildManageRouteWithSchoolYear("/manage/payment-reminders", "", activeSchoolYearStartYear)}
      >{tr("manage:paymentReminderV1.title")}
      </Link>
      {watchdogMessage && <div className="alert alert-success" role="alert"><LocalizedMessage value={watchdogMessage} /></div>}
      {watchdogError && <div className="alert alert-danger" role="alert"><LocalizedMessage isError value={watchdogError} /></div>}
      <ClientSettlementsReportForm
        month={report.month}
        monthOptions={monthOptions}
        clientId={report.clientId}
        clientOptions={clientOptions.map(option => (option.value === 'all' ? { ...option, text: tr('billing:everyone_122') } : option))}
        showDraft={report.showDraft}
        showApproved={report.showApproved}
        showNegativeBalanceOnly={report.showNegativeBalanceOnly}
        showEmptyClients={report.showEmptyClients}
        watchdog={report.watchdog}
        onChange={handleChange}
        onClearFilters={handleClearFilters}
        onApplyFilters={filters => setReport(current => ({ ...current, ...filters }))}
      />
      <div className="manage-text mb-3">{tr("billing:visibleClients_110")} {watchdogFilteredSettlements.length}
      </div>
      {Object.values(summaryLoads).includes('error') ? (
        <div className="alert alert-warning" role="alert">{tr('billing:settlementSummary.error')}</div>
      ) : null}
      {!Object.values(summaryLoads).includes('error') && Object.values(summaryLoads).includes('loading') && (
        <div role="status" className="text-muted mb-3">{tr('billing:settlementSummary.loading')}</div>
      )}
      {Object.values(summaryLoads).every(status => status === 'ready') && (
        <SettlementSummary summary={summarizeSettlements(watchdogFilteredSettlements)} monthLabel={`${monthNames(i18n.language)[Number(report.month.slice(5, 7)) - 1]} ${report.month.slice(0, 4)}`} />
      )}
      {reportWarnings.length > 0 && (
        <div className="alert alert-warning manage-text mt-3" role="alert">
          <div className="d-flex justify-content-between align-items-center">
            <div className="font-weight-bold">
              <i className="fas fa-exclamation-triangle mr-2" aria-hidden="true" />{tr("billing:problemsWereFoundWithStudenttoclientAssignments_111")}
            </div>
            <button
              type="button"
              className="btn btn-link p-0"
              onClick={() => setWarningsExpanded(currentState => !currentState)}
              aria-expanded={warningsExpanded}
            >
              {warningsExpanded ? tr("manage:extraHideWarnings_125") : tr('common:settlementWarnings', { count: reportWarnings.length })}
            </button>
          </div>
          {warningsExpanded && (
            <div className="mt-3">
              {reportWarnings.map(warning => (
                <div key={getMissingClientWarningKey(warning)} className="mb-3">
                  <div>{tr("billing:date_112")} {displayDate(warning.date, i18n.language)} {warning.startTime || ""}</div>
                  <div>lessonRegisterId: {warning.lessonRegisterId}</div>
                  <div>lessonId: {warning.lessonId}</div>
                  <div>studentId: {warning.studentId}</div>
                  <div>{tr("billing:student_116")} {warning.studentRecord ? `${warning.studentRecord.firstName || ""} ${warning.studentRecord.lastName || ""}`.trim() || "brak" : "brak"}
                  </div>
                  <div>{tr("billing:linkedClientId_117")} {warning.studentRecord && warning.studentRecord.clientId ? warning.studentRecord.clientId : "brak"}</div>
                  <div>{tr("billing:studentsRecord_118")} {stringifyWarningRecord(warning.studentRecord)}</div>
                  <div>{tr("billing:lessonregisterstudentsEntry_119")} {stringifyWarningRecord(warning.lessonStudent)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div hidden={watchdogFilteredSettlements.length === 0}>
        <ClientSettlementsList
          renderFees={(settlement, feeOptions) => (
            <SettlementExtraFees
              {...feeOptions}
              settlement={settlement}
              state={feeStates[settlement.clientId] || null}
              students={students.filter(student => !student.deleted && (student.clientId === settlement.clientId || enrollments.some(enrollment => enrollment.clientId === settlement.clientId && !enrollment.deleted && !['cancelled', 'canceled'].includes(enrollment.status) &&
                (enrollment.eligibleStudentIds || enrollment.studentIds || []).includes(student.id))))}
              onMutate={async input => {
                const scopeKey = feeScopeKey;
                const scope = { clientId: settlement.clientId, month: report.month, schoolYearStartYear: activeSchoolYearStartYear };
                try {
                  const result = await settlementService.mutateSettlementFees({ ...input, ...scope });
                  if (activeFeeScope.current === scopeKey) setFeeStates(current => ({ ...current, [settlement.clientId]: result }));
                } catch (error) {
                  if (error.code === 409 || error.statusCode === 409) {
                    const latest = await settlementService.getSettlementFees(scope);
                    if (activeFeeScope.current === scopeKey) setFeeStates(current => ({ ...current, [settlement.clientId]: latest }));
                    error.feesRefreshed = true;
                  }
                  throw error;
                }
              }}
            />
          )}
          settlements={watchdogFilteredSettlements}
          sortConfig={report.sortConfig}
          expansionResetKey={expansionResetKey}
          initialExpansionState={expansionState.current}
          onExpansionChange={rememberExpansion}
          getPersonLinkProps={getPersonLinkProps}
          onSort={handleSort}
          onToggleExclusion={handleToggleExclusion}
          onApprove={handleApprove}
          onOpenPayments={handleOpenPayments}
          watchdogDrafts={watchdogDrafts}
          onWatchdogDraftChange={handleWatchdogDraftChange}
          onSaveWatchdogFollowUp={handleSaveWatchdogFollowUp}
        />
      </div>
      {watchdogFilteredSettlements.length === 0 && (
        <div className="alert alert-light border manage-text">{tr("billing:noClientsMatchTheSelectedFilters_120")}</div>
      )}
      {approvalError && !approval && <div className="alert alert-danger" role="alert">{approvalError}</div>}
      {feeLoadError && (
      <div className="alert alert-danger" role="alert">Nie udało się wczytać opłat dodatkowych. Podsumowanie może być niepełne.
        <button type="button" className="btn btn-link" onClick={() => setFeeReload(value => value + 1)}>Spróbuj ponownie</button>
      </div>
      )}
      {approval && (
      <Modal
        show
        onHide={() => { if (!approvalBusy) { setApproval(null); setApprovalError(''); } }}
        onEntered={node => {
          node.removeAttribute('aria-hidden');
          node.setAttribute('aria-labelledby', 'settlement-approval-title');
        }}
      >
        <Modal.Header closeButton><Modal.Title id="settlement-approval-title">{tr('billing:settlementApproval.title')}</Modal.Title></Modal.Header>
        <Modal.Body>
          <p>{tr('billing:settlementApproval.hint')}</p>
          {approval && <p><strong>{approval.settlement.clientName}</strong> · {approval.input.month}</p>}
          {approval && <StudentSettlementSummary settlement={approval.settlement} />}
          {approval && <p><strong>{tr('billing:draftTotal_107')} {formatLedgerAmount(approval.settlement.monthlyChargeAmount, i18n.language)}</strong></p>}
          {approvalError && <div className="alert alert-danger" role="alert">{approvalError}</div>}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn manage-button manage-teal-button" disabled={approvalBusy} onClick={() => { setApproval(null); setApprovalError(''); }}>{tr('common:cancel')}</button>
          {approvalConflict ? (
            <button type="button" className="btn manage-button manage-red-button" disabled={approvalBusy} onClick={refreshApproval}>{tr('billing:settlementApproval.refresh')}</button>
          ) : <button type="button" className="btn manage-button manage-red-button" disabled={approvalBusy} onClick={confirmApproval}>{tr('billing:settlementApproval.confirm')}</button>}
        </Modal.Footer>
      </Modal>
      )}
      <ClientPaymentsModal
        show={paymentsModal.isOpen}
        clientName={selectedClient ? `${selectedClient.firstName || ""} ${selectedClient.lastName || ""}`.trim() : ""}
        clientId={paymentsModal.clientId}
        month={paymentsModal.month || report.month}
        schoolYearStartYear={activeSchoolYearStartYear}
        entries={modalEntries}
        onClose={handleClosePayments}
        onSave={saveClientLedgerEntry}
        onDelete={deleteClientLedgerEntry}
      />
    </div>
  );
}

ClientSettlementsReportPage.propTypes = {
  clients: PropTypes.array.isRequired,
  students: PropTypes.array.isRequired,
  teachers: PropTypes.array,
  schoolClasses: PropTypes.array.isRequired,
  lessonRegister: PropTypes.array.isRequired,
  enrollments: PropTypes.array,
  clientLedger: PropTypes.array.isRequired,
  clientOptions: PropTypes.array,
  classStudents: PropTypes.array,
  loadClients: PropTypes.func.isRequired,
  loadStudents: PropTypes.func.isRequired,
  loadClassStudents: PropTypes.func,
  loadTeachers: PropTypes.func,
  loadSchoolClasses: PropTypes.func.isRequired,
  loadLessonRegisters: PropTypes.func.isRequired,
  loadEnrollments: PropTypes.func,
  loadClientLedger: PropTypes.func.isRequired,
  saveClientLedgerEntry: PropTypes.func,
  deleteClientLedgerEntry: PropTypes.func,
  replaceMonthlyCharge: PropTypes.func.isRequired,
  activeSchoolYearStartYear: PropTypes.number,
  principalKey: PropTypes.string,
  discountService: PropTypes.object,
  settlementService: PropTypes.object,
  settingsService: PropTypes.shape({
    getSettings: PropTypes.func.isRequired,
    saveSetting: PropTypes.func.isRequired,
  }),
};

ClientSettlementsReportPage.defaultProps = {
  principalKey: '',
  teachers: [],
  enrollments: [],
  classStudents: [],
  clientOptions: [{ value: "all", text: "Wszyscy" }],
  loadTeachers: () => Promise.resolve(),
  loadClassStudents: () => Promise.resolve(),
  loadEnrollments: defaultLoadEnrollments,
  saveClientLedgerEntry: () => Promise.resolve(),
  deleteClientLedgerEntry: () => Promise.resolve(),
  activeSchoolYearStartYear: getCurrentSchoolYearStartYear(),
  settingsService: SettingsService,
  discountService: ClientDiscountService,
  settlementService: ClientLedgerService,
};

const mapStateToProps = state => ({
  principalKey: getPrincipalKey(state.user),
  clients: state.clients,
  students: state.students,
  classStudents: state.classStudents,
  teachers: state.teachers,
  schoolClasses: state.schoolClasses,
  lessonRegister: state.lessonRegister,
  enrollments: state.enrollments,
  clientLedger: state.clientLedger,
  clientOptions: [
    { value: "all", text: "Wszyscy" },
    ...(state.clients || [])
      .filter(client => !client.deleted)
      .map(client => ({
        value: client.id,
        text: `${client.firstName || ""} ${client.lastName || ""}`.trim(),
      })),
  ],
  activeSchoolYearStartYear: state.settings.activeSchoolYearStartYear,
});

export default connect(mapStateToProps, {
  loadClients: clientActions.loadClients,
  loadStudents: studentActions.loadStudents,
  loadClassStudents: classStudentsActions.loadClassStudents,
  loadTeachers: teacherActions.loadTeachers,
  loadSchoolClasses: schoolClassActions.loadSchoolClasses,
  loadLessonRegisters: lessonRegisterActions.loadLessonRegisters,
  loadEnrollments: enrollmentActions.loadEnrollments,
  loadClientLedger: clientLedgerActions.loadClientLedger,
  saveClientLedgerEntry: clientLedgerActions.saveClientLedgerEntry,
  deleteClientLedgerEntry: clientLedgerActions.deleteClientLedgerEntry,
  replaceMonthlyCharge: clientLedgerActions.replaceMonthlyCharge,
})(ClientSettlementsReportPage);
